import { useEffect, useState } from "react";
import { 
    Box, Image, Text, Heading, Button, Input, Grid, GridItem, Modal, 
    ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, 
    ModalCloseButton, useDisclosure, useToast
} from "@chakra-ui/react";
import { jwtDecode } from "jwt-decode";

const Faces = () => {
    const [faces, setFaces] = useState([]);
    const [selectedFace, setSelectedFace] = useState({ name: '', photo: null, imageUrl: '' });
    const [userLevel, setUserLevel] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [userId, setUserId] = useState(null);
    const toast = useToast();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const decoded = jwtDecode(token);
            setUserLevel(decoded.role);
            setUserId(decoded.userId);
        }
        fetchFaces();
    }, []);

    const fetchFaces = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/faces', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar faces');
            const data = await response.json();

            // Adicionar a URL da imagem convertida em Blob
            const facesWithBlobUrls = await Promise.all(
                data.map(async (face) => ({
                    ...face,
                    imageUrl: await fetchImageWithAuth(face.image_url),
                }))
            );

            setFaces(facesWithBlobUrls);
        } catch (error) {
            toast({
                title: "Erro ao buscar faces",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchImageWithAuth = async (imageName) => {
        try {
            console.log(imageName);
            // Extrai apenas o nome do arquivo se for um path completo
            const actualFilename = (imageName.includes('/') || imageName.includes('\\'))
                ? imageName.split('/').pop().split('\\').pop()
                : imageName;
            
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/faces/image/${actualFilename}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!response.ok) throw new Error('Erro ao buscar imagem');
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error('Erro ao carregar imagem:', error);
            return '';
        }
    };

    const handleOpenModal = () => {
        setSelectedFace({ name: '', photo: null, imageUrl: '' });
        onOpen();
    };

    const handleDeleteFace = async (faceId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/faces/${faceId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao remover rosto');
            fetchFaces();
        } catch (error) {
            console.error('Erro:', error);
        }
    };

    const handleSaveFace = async () => {
        const formData = new FormData();
        formData.append('name', selectedFace.name);
        formData.append('user_id', userId);

        if (selectedFace.photo && selectedFace.photo instanceof File) {
            formData.append('image', selectedFace.photo);
        } else {
            console.error('Erro: Foto inválida');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/faces', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) throw new Error('Erro ao adicionar rosto');

            fetchFaces();
            onClose();
        } catch (error) {
            console.error('Erro:', error);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFace({ ...selectedFace, photo: file, imageUrl: URL.createObjectURL(file) });
    };

    return (
        <Box p={6} flex="1" minH="100vh" bg="gray.100" justify="center">
            <Heading mb={4}>
                Faces
                {userLevel >= 2 && (
                    <Button onClick={handleOpenModal} ml={4} colorScheme="teal">Adicionar Face</Button>
                )}
            </Heading>
            <Text fontSize="xl" mb={4}>Lista de Faces:</Text>
            <Grid templateColumns="repeat(8, 1fr)" gap={4} justifyContent="center">
                {faces.map((face) => (
                    <GridItem key={face.id} w="100%" bg="white" boxShadow="md" p={4} borderRadius="md" textAlign="center">
                        <Text fontSize="lg" fontWeight="bold" mb={2}>{face.name}</Text>
                        <Image
                            src={face.imageUrl}
                            alt={face.name}
                            boxSize="150px"
                            objectFit="cover"
                            mx="auto"
                            mb={3}
                            borderRadius={5}
                        />
                        {userLevel >= 2 && (
                            <Button colorScheme="red" onClick={() => handleDeleteFace(face.id)}>Remover</Button>
                        )}
                    </GridItem>
                ))}
            </Grid>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Adicionar Nova Face</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input
                            value={selectedFace.name}
                            name="name"
                            onChange={(e) => setSelectedFace({ ...selectedFace, name: e.target.value })}
                            placeholder="Nome do Rosto"
                            variant="outline"
                            mb={4}
                        />
                        <Input
                            type="file"
                            name="photo"
                            onChange={handleFileChange}
                            placeholder="Foto do Rosto"
                            variant="outline"
                            mb={4}
                        />
                        {selectedFace.imageUrl && (
                            <Image src={selectedFace.imageUrl} alt="Preview" boxSize="150px" objectFit="cover" mb={4} mx="auto" />
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="teal" onClick={handleSaveFace}>Adicionar</Button>
                        <Button onClick={onClose} ml={3}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Faces;
